import LoginContext, { LoginProvider, ELoginUiState } from '../../contexts/LoginContext';
import { Redirect } from 'react-router';
import { TMigrateAccountRouterState } from '../../contexts/MigrateAccountContext';
import { TTermsRouterState } from '../../contexts/TermsContext';
import GlobalContext from '../../contexts/GlobalContext';
import { TMigrateStaffAccountRouterState } from '../../contexts/MigrateStaffAccountContext';
import { TMultiFactorAuthRouterState } from '../../contexts/MultiFactorAuthContext';
import { FC, ReactElement, useContext } from 'react';
import { Stack } from '@mui/material';
import LoginStaff from '../../components/LoginStaff/LoginStaff';
import LoginConsumerEmail from '../../components/LoginConsumerEmail/LoginConsumerEmail';
import { Loader } from '../../components/Loader/Loader';
import PageLayoutV2 from '../../layout/PageLayoutV2/PageLayoutV2';
import LoginConsumer from '../../components/LoginConsumer/LoginConsumer';
import { TCreateAccountRouterState } from '../../contexts/CreateAccountContext';
import { ERoutes } from '../../util/constants';
import { Route, Switch } from 'react-router-dom';
import LoginThirdParty from '../../components/LoginThirdparty/LoginThirdParty';
import { TUnverifiedRouterState } from '../../contexts/UnverifiedAccountContext';
import { TMigrateToExistingAccountRouterState } from '../../contexts/MigrateToExistingAccountContext';
import FacebookWarning from '../../components/FacebookWarning/FacebookWarning';

const LoginRouter: FC = () => {
    const { isStaffLogin } = useContext(GlobalContext);
    const { loginProvider, username } = useContext(LoginContext);

    return (
        <Switch>
            <Route exact path={ERoutes.login}>
                {isStaffLogin ? <LoginStaff /> : <LoginConsumerEmail />}
            </Route>
            <Route path={ERoutes.loginConsumer}>
                {!username ? <Redirect to={ERoutes.login} /> : <LoginConsumer />}
            </Route>
            <Route path={ERoutes.loginExternal}>
                {!loginProvider ? <Redirect to={ERoutes.login} /> : <LoginThirdParty />}
            </Route>
            <Route exact path={ERoutes.loginFacebook}>
                <FacebookWarning />
            </Route>
        </Switch>
    );
};

const LoginUi: FC = () => {
    const { isStaffLogin } = useContext(GlobalContext);
    const { loginUiState, username, userId, migrationUser, migrationUserExistingAccount, redirectURL } =
        useContext(LoginContext);
    let loginUI: ReactElement;
    switch (loginUiState) {
        case ELoginUiState.default:
        case ELoginUiState.queryStringParsed:
            loginUI = (
                <PageLayoutV2 showFooter={false}>
                    <Stack direction={'row'} sx={{ justifyContent: 'center' }}>
                        <Loader />
                    </Stack>
                </PageLayoutV2>
            );
            break;
        case ELoginUiState.ready:
        case ELoginUiState.error:
            loginUI = <LoginRouter />;
            break;
        case ELoginUiState.loginConsumer:
            loginUI = isStaffLogin ? (
                <>
                    <Redirect to={ERoutes.error} />
                    {console.error(`Invalid login ui state for staff user -- ${loginUiState}`)}
                </>
            ) : (
                <>
                    <Redirect push to={ERoutes.loginConsumer} />
                    <LoginRouter />
                </>
            );
            break;
        case ELoginUiState.createAccount:
            loginUI = isStaffLogin ? (
                <>
                    <Redirect to={ERoutes.error} />
                    {console.error(`Invalid login ui state for staff user -- ${loginUiState}`)}
                </>
            ) : (
                <Redirect
                    push
                    to={{
                        pathname: ERoutes.createAccount,
                        state: { prePopulateForm: { email: username } } as TCreateAccountRouterState,
                    }}
                />
            );
            break;
        case ELoginUiState.thirdPartyLogin:
            loginUI = isStaffLogin ? (
                <>
                    <Redirect to={ERoutes.error} />
                    {console.error(`Invalid login ui state for staff user -- ${loginUiState}`)}
                </>
            ) : (
                <>
                    <Redirect push to={ERoutes.loginExternal} />
                    <LoginRouter />
                </>
            );
            break;
        case ELoginUiState.deprecateFacebook:
            loginUI = isStaffLogin ? (
                <>
                    <Redirect to={ERoutes.error} />
                    {console.error(`Invalid login ui state for staff user -- ${loginUiState}`)}
                </>
            ) : (
                <>
                    <Redirect push to={ERoutes.loginFacebook} />
                    <LoginRouter />
                </>
            );
            break;
        case ELoginUiState.loginSuccess:
            loginUI = (
                <>
                    <PageLayoutV2 showFooter={false}>
                        <Stack direction={'row'} sx={{ justifyContent: 'center' }}>
                            <Loader />
                        </Stack>
                    </PageLayoutV2>
                    {window.location.assign(redirectURL)}
                </>
            );
            break;
        case ELoginUiState.requireAgreement:
            const termsRouterState: TTermsRouterState = { redirectUrl: redirectURL };
            loginUI = <Redirect to={{ pathname: ERoutes.terms, state: termsRouterState }} />;
            break;
        case ELoginUiState.requireMfa:
            const mfaRouterState: TMultiFactorAuthRouterState = { redirectUrl: redirectURL };
            loginUI = <Redirect to={{ pathname: ERoutes.multiFactorAuthValidate, state: mfaRouterState }} />;
            break;
        case ELoginUiState.unverified:
            const unverifiedRouterState: TUnverifiedRouterState = { email: username, userId };
            loginUI = <Redirect to={{ pathname: ERoutes.unverified, state: unverifiedRouterState }} />;
            break;
        case ELoginUiState.unverifiedStaff:
            loginUI = <Redirect to={{ pathname: ERoutes.unverifiedStaff, state: { email: username } }} />;
            break;
        case ELoginUiState.migrationRequired:
            loginUI = (
                <Redirect
                    push
                    to={{
                        pathname: ERoutes.migrateAccount,
                        state: { migrationUser } as TMigrateAccountRouterState,
                    }}
                />
            );
            break;
        case ELoginUiState.staffMigrationRequired:
            loginUI = (
                <Redirect
                    push
                    to={{
                        pathname: ERoutes.staffMigrateAccount,
                        state: { migrationUser } as TMigrateStaffAccountRouterState,
                    }}
                />
            );
            break;
        case ELoginUiState.migrationRequiredExistingAccount:
            loginUI = (
                <Redirect
                    push
                    to={{
                        pathname: ERoutes.migrateToExistingAccount,
                        state: migrationUserExistingAccount as TMigrateToExistingAccountRouterState,
                    }}
                />
            );
            break;
        default:
            loginUI = (
                <>
                    {console.error('Invalid login ui state')}
                    <Redirect to={ERoutes.error} />
                </>
            );
    }

    return loginUI;
};

const LoginScreen: FC = () => {
    return (
        <LoginProvider>
            <LoginUi />
        </LoginProvider>
    );
};

export default LoginScreen;
